import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useSmoothScroll } from "@/lib/smooth-scroll";
import logo from "@/assets/inkwell-logo.png";

const LINKS = [
  { label: "The Work", href: "#work", testid: "nav-link-work" },
  { label: "About", href: "#about", testid: "nav-link-about" },
  { label: "At Last", href: "#at-last", testid: "nav-link-at-last" },
  { label: "Insurance", href: "#insurance", testid: "nav-link-insurance" },
  { label: "Papers", href: "#papers", testid: "nav-link-papers" },
  { label: "Journal", href: "#journal", testid: "nav-link-journal" },
  { label: "Contact", href: "#contact", testid: "nav-link-contact" },
];

export function Nav() {
  const [open, setOpen] = useState(false);
  const { scrollTo } = useSmoothScroll();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (href: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    scrollTo(href);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <nav
        aria-label="Primary"
        className="border-b border-ivory/10 bg-inkblack/95 backdrop-blur-xl"
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 sm:px-8 lg:px-12">
          <a
            href="#top"
            onClick={go("#top")}
            data-testid="nav-logo"
            aria-label="The Inkwell Therapy — home"
            className="block w-[150px] sm:w-[190px]"
          >
            <img
              src={logo}
              alt="The Inkwell — A Marriage and Family Therapy Practice"
              className="h-auto w-full"
            />
          </a>

          <div className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={go(l.href)}
                data-testid={l.testid}
                className="font-mono text-[11px] tracking-[0.22em] uppercase text-ivory/70 transition-colors duration-300 hover:text-ivory"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#begin"
              onClick={go("#begin")}
              data-testid="nav-cta-begin"
              className="rounded-sm border border-gold/60 px-5 py-2 font-mono text-[11px] tracking-[0.22em] uppercase text-gold transition-colors duration-300 hover:bg-gold hover:text-inkblack"
            >
              Begin
            </a>
          </div>

          <button
            type="button"
            data-testid="nav-menu-toggle"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
            className="rounded-full p-2 text-ivory lg:hidden"
          >
            {open ? <Menu className="hidden" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col bg-inkblack/[0.97] backdrop-blur-2xl lg:hidden"
          >
            <div className="flex h-20 items-center justify-between px-6">
              <img src={logo} alt="The Inkwell" className="w-[150px]" />
              <button
                type="button"
                data-testid="nav-menu-close"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="rounded-full p-2 text-ivory"
              >
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="flex flex-1 flex-col justify-center gap-2 px-8">
              {[...LINKS, { label: "Begin Therapy", href: "#begin", testid: "nav-link-begin-mobile" }].map(
                (l, i) => (
                  <motion.a
                    key={l.href + l.label}
                    href={l.href}
                    onClick={go(l.href)}
                    data-testid={`${l.testid}-mobile`}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 + i * 0.06, duration: 0.5 }}
                    className="border-b border-ivory/10 py-4 font-serif text-3xl text-ivory transition-colors hover:text-gold"
                  >
                    {l.label}
                  </motion.a>
                ),
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
